import { useQuery } from '@tanstack/react-query';
import { API_BASE } from '../api-base';
import { apiFetch } from '../api-client';
import { useMe } from '../me';
import { queryKeys } from '../query-client';
import type { CalendarItem } from './calendar-item';
import { liveCardBooking, type CalendarItemCard } from './calendar-item-card';
import { bookingFromReply } from './calendar-normalize';
import { calendarOwnerKey } from './calendar-storage';

const STALE_MS = 60_000;

/**
 * One booking as the harness reads it now, by the ids a chat card stores; `null` when there is none, since a query that
 * resolves `undefined` is an error to React Query and would keep the booking it read before.
 */
export async function fetchCardBooking(siteId: string, bookingId: string): Promise<CalendarItem | null> {
  const query = new URLSearchParams({ siteId, bookingId });
  const res = await apiFetch(`${API_BASE}/api/calendar/booking?${query}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`calendar booking ${res.status}`);
  return bookingFromReply(await res.json()) ?? null;
}

/**
 * The booking a chat card names, read live by its ids, so the card's buttons follow the booking as it is now rather than
 * as it was when the card was sent.
 */
export function useLiveCardBooking(card: Pick<CalendarItemCard, 'kind' | 'bookingRef'>): {
  item?: CalendarItem;
  loading: boolean;
  /** When the booking was last read, and when a read of it last failed (0 for never). */
  readAt: number;
  failedAt: number;
  reread: () => void;
} {
  const ownerKey = calendarOwnerKey(useMe());
  const ref = card.kind === 'booking' ? card.bookingRef : undefined;
  const booking = useQuery({
    queryKey: queryKeys.calendarBooking(ownerKey, ref?.siteId ?? '', ref?.bookingId ?? ''),
    queryFn: () => fetchCardBooking(ref?.siteId ?? '', ref?.bookingId ?? ''),
    enabled: !!ownerKey && !!ref,
    staleTime: STALE_MS,
  });
  const item = liveCardBooking(card, booking.data ?? undefined);
  return {
    ...(item ? { item } : {}),
    loading: booking.isLoading,
    readAt: booking.dataUpdatedAt,
    failedAt: booking.isError ? booking.errorUpdatedAt : 0,
    reread: () => void booking.refetch(),
  };
}
